/**
 * Página pública de assinatura do funcionário: chega pelo link do e-mail, sem login. Três passos, nesta ordem — abrir o
 * documento, pedir o código (vai para o mesmo e-mail) e confirmar com CPF + código. O servidor só aceita o código depois
 * que o documento foi aberto.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type React from 'react';
import { useState } from 'react';
import type { AssinaturaPublica } from '@sgc/contracts';
import { get, post } from '../lib/api';
import { Campo, Check } from '../ui/campos';

export const Route = createFileRoute('/assinar/$token')({ component: Assinar });

function Assinar() {
  const { token } = Route.useParams();
  const qc = useQueryClient();
  const chave = ['assinar', token];
  const q = useQuery({ queryKey: chave, queryFn: () => get<AssinaturaPublica>(`/v1/assinar/${token}`), retry: false });
  const [cpf, setCpf] = useState('');
  const [codigo, setCodigo] = useState('');
  const [aceito, setAceito] = useState(false);
  const recarregar = () => void qc.invalidateQueries({ queryKey: chave });
  const pedirCodigo = useMutation({ mutationFn: () => post<{ expiraEm: string }>(`/v1/assinar/${token}/codigo`), onSuccess: recarregar });
  const assinar = useMutation({ mutationFn: () => post(`/v1/assinar/${token}/assinar`, { codigo, cpf: cpf || null, aceito: true }), onSuccess: recarregar });
  const d = q.data;
  const cor = d?.corPrimaria && /^#[0-9a-fA-F]{6}$/.test(d.corPrimaria) ? d.corPrimaria : null;
  const erro = (e: unknown) => (e instanceof Error ? e.message : 'não deu certo — tente de novo');

  return (
    <div className="portal" style={cor ? ({ '--accent': cor, '--accent-hover': cor, '--accent-text': cor } as React.CSSProperties) : undefined}>
      <header className="portal-topo"><div className="marca">{d?.logoUrl ? <img src={d.logoUrl} alt={d.escritorio} style={{ maxHeight: 40, maxWidth: 160 }} /> : <span className="logo-mark">{(d?.escritorio ?? 'SGC').slice(0, 3).toUpperCase()}</span>}<b>{d?.escritorio ?? 'Assinatura de documento'}</b></div></header>
      <main className="portal-main">
        <div className="portal-cartao" style={{ maxWidth: 620, margin: '24px auto' }}>
          {q.isLoading ? <p>carregando…</p> : q.isError || !d ? (
            <><h2>Link inválido</h2><p>{erro(q.error)}</p><p className="mini">O link de assinatura é pessoal e tem validade. Se precisar, peça à empresa para enviar de novo.</p></>
          ) : d.assinadoEm ? (
            <>
              <h2>Documento assinado ✓</h2>
              <p><b>{d.documento}</b>, de {d.empresa}, foi assinado por <b>{d.signatario}</b> em {new Date(d.assinadoEm).toLocaleString('pt-BR')}.</p>
              <div className="linha-acoes" style={{ marginTop: 12 }}>
                <a className="btn primario" href={`/v1/assinar/${token}/comprovante`} target="_blank" rel="noreferrer">Baixar o comprovante</a>
                <a className="btn" href={`/v1/assinar/${token}/documento`} target="_blank" rel="noreferrer">Ver o documento</a>
              </div>
              <p className="mini" style={{ marginTop: 12 }}>Guarde o comprovante: ele traz a data, o endereço de onde você assinou e o código que identifica o arquivo.</p>
            </>
          ) : (
            <>
              <h2>Documento para você assinar</h2>
              <p>Olá, <b>{d.signatario}</b>. <b>{d.empresa}</b> enviou o documento <b>{d.documento}</b> para você ler e assinar.</p>
              <ol className="passos-assinar">
                <li className={d.visto ? 'feito' : 'atual'}>
                  <b>1. Leia o documento</b>
                  <div><a className={`btn${d.visto ? '' : ' primario'}`} href={`/v1/assinar/${token}/documento`} target="_blank" rel="noreferrer" onClick={() => setTimeout(recarregar, 1500)}>{d.visto ? 'Abrir de novo' : 'Abrir o documento'}</a></div>
                </li>
                <li className={!d.visto ? 'bloqueado' : d.codigoEnviado ? 'feito' : 'atual'}>
                  <b>2. Receba o código no seu e-mail</b>
                  <div className="mini">vai para {d.emailMascarado} e vale 15 minutos</div>
                  <div><button className={`btn${d.visto && !d.codigoEnviado ? ' primario' : ''}`} type="button" disabled={!d.visto || pedirCodigo.isPending} onClick={() => pedirCodigo.mutate()}>{pedirCodigo.isPending ? 'enviando…' : d.codigoEnviado ? 'Enviar outro código' : 'Enviar o código'}</button></div>
                  {pedirCodigo.isError && <div className="alerta erro">{erro(pedirCodigo.error)}</div>}
                </li>
                <li className={d.codigoEnviado ? 'atual' : 'bloqueado'}>
                  <b>3. Confirme e assine</b>
                  <form className="fgrid" style={{ marginTop: 8 }} onSubmit={(e) => { e.preventDefault(); assinar.mutate(); }}>
                    {d.pedeCpf && <Campo className="c6" rotulo="Seu CPF" valor={cpf} onChange={(v) => setCpf(v.replace(/[^\d.-]/g, '').slice(0, 14))} inputMode="numeric" placeholder="000.000.000-00" disabled={!d.codigoEnviado} />}
                    <Campo className={d.pedeCpf ? 'c6' : 'c12'} rotulo="Código de 6 dígitos" valor={codigo} onChange={(v) => setCodigo(v.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" disabled={!d.codigoEnviado} />
                    <Check className="c12" rotulo="Li o documento e concordo com o seu conteúdo" valor={aceito} onChange={setAceito} />
                    {assinar.isError && <div className="c12 alerta erro">{erro(assinar.error)}</div>}
                    <div className="c12"><button className="btn primario" type="submit" disabled={!d.codigoEnviado || codigo.length !== 6 || !aceito || (d.pedeCpf && cpf.replace(/\D/g, '').length !== 11) || assinar.isPending}>{assinar.isPending ? 'assinando…' : 'Assinar o documento'}</button></div>
                  </form>
                </li>
              </ol>
              <p className="mini" style={{ marginTop: 12 }}>Não reconhece este documento ou não concorda? Não assine — fale com a empresa. A assinatura fica registrada com data, hora e o endereço de onde foi feita.</p>
            </>
          )}
        </div>
      </main>
      <footer className="portal-rodape">Assinatura eletrônica · código do arquivo: <span className="mono">{d?.sha256.slice(0, 16) ?? '—'}…</span></footer>
    </div>
  );
}
